import type { Medication } from '@/types/data'
import type { StateCreator } from 'zustand'
import type { MedicationRequestStore } from './medication-request'

/** A decision recorded on a card; nothing is sent until Submit decision. */
export type TreatmentDecision = {
  action: 'approve' | 'reject'
  reason: string
  /** Reject only: whether the reason goes to the patient as a rejection conversation. */
  startConversation?: boolean
  /** Reject only: whether CS should contact the patient. */
  csNeedToContactPatient?: boolean
}

/** The treatment (med request id) the Clinical decision panel is open for, and the action being decided. */
export type DecisionPanelTarget = { treatmentId: string; action: TreatmentDecision['action'] }

/** Treatments 2..N, one per other item; Treatment 1 and the shared issue date stay on the flat store fields. */
export type MultiProductOrderTreatment = {
  id: string
  medication: Medication | null
  direction: string
}

export type MultiProductOrderSlice = {
  multiProductOrderTreatments: MultiProductOrderTreatment[]
  updateMultiProductOrderTreatment: (id: string, patch: Partial<Omit<MultiProductOrderTreatment, 'id'>>) => void
  /** Guards re-seeding on refetches of the same request; reset clears it for the next one. */
  multiProductOrderSeededRequestId: string | null
  seedMultiProductOrderTreatments: (requestId: string, treatments: MultiProductOrderTreatment[]) => void
  /** Set while a card's decision is being made in the panel. */
  decisionPanelTarget: DecisionPanelTarget | null
  setDecisionPanelTarget: (decisionPanelTarget: DecisionPanelTarget | null) => void
  /** Recorded decisions by treatment (med request) id, awaiting Submit decision. */
  treatmentDecisions: Record<string, TreatmentDecision>
  setTreatmentDecision: (treatmentId: string, decision: TreatmentDecision) => void
  undoTreatmentDecision: (treatmentId: string) => void
  /** Drops the open panel and every recorded decision. */
  clearTreatmentDecisions: () => void
  /** True from Submit decision until the page shows the sent decisions. */
  isTreatmentDecisionSubmitting: boolean
  setTreatmentDecisionSubmitting: (isTreatmentDecisionSubmitting: boolean) => void
}

export const createMultiProductOrderSlice: StateCreator<
  MedicationRequestStore,
  [],
  [],
  MultiProductOrderSlice
> = set => ({
  multiProductOrderTreatments: [],
  updateMultiProductOrderTreatment: (id: string, patch: Partial<Omit<MultiProductOrderTreatment, 'id'>>) =>
    set(state => ({
      multiProductOrderTreatments: state.multiProductOrderTreatments.map(treatment =>
        treatment.id === id ? { ...treatment, ...patch } : treatment
      )
    })),
  multiProductOrderSeededRequestId: null,
  seedMultiProductOrderTreatments: (requestId: string, treatments: MultiProductOrderTreatment[]) =>
    set({
      multiProductOrderSeededRequestId: requestId,
      multiProductOrderTreatments: treatments,
      decisionPanelTarget: null,
      treatmentDecisions: {}
    }),
  decisionPanelTarget: null,
  setDecisionPanelTarget: (decisionPanelTarget: DecisionPanelTarget | null) => set({ decisionPanelTarget }),
  treatmentDecisions: {},
  setTreatmentDecision: (treatmentId: string, decision: TreatmentDecision) =>
    set(state => ({ treatmentDecisions: { ...state.treatmentDecisions, [treatmentId]: decision } })),
  undoTreatmentDecision: (treatmentId: string) =>
    set(state => ({
      treatmentDecisions: Object.fromEntries(
        Object.entries(state.treatmentDecisions).filter(([id]) => id !== treatmentId)
      )
    })),
  clearTreatmentDecisions: () => set({ decisionPanelTarget: null, treatmentDecisions: {} }),
  isTreatmentDecisionSubmitting: false,
  setTreatmentDecisionSubmitting: (isTreatmentDecisionSubmitting: boolean) => set({ isTreatmentDecisionSubmitting })
})
